'use client';

import { useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import {
  Comment,
  deleteComment,
  editComment,
  postComment,
  reportComment,
  toggleCommentLike
} from '@/app/actions/comments';
import { useToast } from '@/components/ui/Toasts/use-toast';
import { useAuthGuard } from '@/components/ui/auth-guard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { AnimatePresence, motion } from 'framer-motion';
import {
  Bold,
  Edit2,
  Flag,
  Heart,
  Italic,
  List,
  MessageCircle,
  MoreVertical,
  Quote,
  Share2,
  Trash2
} from 'lucide-react';

// Simple date formatter utility
function formatDistanceToNow(dateInput: Date | string): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  return date.toLocaleDateString();
}

interface CommentSectionProps {
  postSlug: string;
  initialComments: Comment[];
}

export function CommentSection({
  postSlug,
  initialComments
}: CommentSectionProps) {
  const { isAuthenticated, user } = useAuthGuard();
  const { toast } = useToast();
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [newComment, setNewComment] = useState('');
  const [isPending, setIsPending] = useState(false);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [StarterKit],
    content: '',
    onUpdate: ({ editor }) => {
      setNewComment(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert min-h-[100px] w-full px-4 py-3 bg-transparent text-foreground placeholder-muted-foreground focus:outline-none resize-none'
      }
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const textContent = editor?.getText() || '';
    if (!textContent.trim() || !isAuthenticated) return;

    setIsPending(true);

    toast.promise(postComment(postSlug, newComment), {
      loading: 'Publishing your thoughts...',
      success: (result) => {
        if (result.success) {
          // Update the UI locally
          const optimisticComment: Comment = {
            id: `temp-${Date.now()}`,
            post_slug: postSlug,
            user_id: user?.id || 'unknown',
            content: newComment.trim(),
            created_at: new Date().toISOString(),
            likesCount: 0,
            isLiked: false,
            users: {
              full_name: user?.name || 'You',
              avatar_url: user?.image || null
            }
          };

          setComments((prev) => [optimisticComment, ...prev]);
          setNewComment('');
          editor?.commands.setContent('');
          setIsPending(false);
          return 'Comment posted successfully!';
        }
        setIsPending(false);
        throw new Error(result.error || 'Failed to post comment');
      },
      error: (err) => {
        setIsPending(false);
        return err instanceof Error ? err.message : 'Failed to post comment';
      }
    });
  };

  const toggleLikeRequest = async (commentId: string) => {
    if (!isAuthenticated) {
      toast.error('Authentication required', {
        description: 'Please sign in to like comments'
      });
      return;
    }

    // The actual update happens within the CommentItem for better responsiveness
    return toggleCommentLike(commentId, postSlug);
  };

  const handleDelete = async (commentId: string) => {
    toast.promise(deleteComment(commentId, postSlug), {
      loading: 'Deleting comment...',
      success: (result) => {
        if (result.success) {
          setComments((prev) => prev.filter((c) => c.id !== commentId));
          return 'Comment deleted successfully';
        }
        throw new Error(result.error);
      },
      error: 'Failed to delete comment'
    });
  };

  const handleReport = async (commentId: string) => {
    toast.promise(reportComment(commentId, postSlug), {
      loading: 'Reporting comment...',
      success: (result) => {
        if (result.success) {
          return 'Comment reported. Our administration team will review it.';
        }
        throw new Error(result.error);
      },
      error: 'Failed to report comment'
    });
  };

  const handleEdit = async (commentId: string, newContent: string) => {
    toast.promise(editComment(commentId, postSlug, newContent), {
      loading: 'Updating comment...',
      success: (result) => {
        if (result.success) {
          setComments((prev) =>
            prev.map((c) =>
              c.id === commentId
                ? {
                    ...c,
                    content: newContent,
                    updated_at: new Date().toISOString()
                  }
                : c
            )
          );
          return 'Comment updated successfully';
        }
        throw new Error(result.error);
      },
      error: 'Failed to update comment'
    });
  };

  return (
    <section className='border-border mt-12 space-y-8 border-t py-8'>
      <div>
        <h2 className='text-foreground mb-6 text-2xl font-bold'>
          Discussion ({comments.length})
        </h2>

        {/* New comment form */}
        {isAuthenticated ? (
          <form
            onSubmit={handleSubmit}
            className='mb-8 space-y-4'>
            <div className='flex items-start gap-4'>
              <div className='flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-emerald-100 text-emerald-700'>
                {user?.image ? (
                  <Image
                    src={user.image}
                    alt={user.name || 'Avatar'}
                    width={40}
                    height={40}
                    className='h-full w-full object-cover'
                  />
                ) : (
                  <span className='text-sm font-semibold'>
                    {user?.name?.charAt(0) ||
                      user?.email?.charAt(0)?.toUpperCase() ||
                      '?'}
                  </span>
                )}
              </div>
              <div className='border-border bg-card focus-within:ring-primary/40 flex-1 overflow-hidden rounded-xl border shadow-sm transition-all focus-within:ring-2'>
                <div className='bg-muted/40 border-border/50 flex items-center gap-1 border-b px-3 py-2'>
                  <button
                    type='button'
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    className={`hover:bg-muted/80 rounded-md p-1.5 transition-colors ${editor?.isActive('bold') ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}
                    title='Bold'>
                    <Bold className='size-4' />
                  </button>
                  <button
                    type='button'
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    className={`hover:bg-muted/80 rounded-md p-1.5 transition-colors ${editor?.isActive('italic') ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}
                    title='Italic'>
                    <Italic className='size-4' />
                  </button>
                  <div className='bg-border mx-1 h-4 w-px' />
                  <button
                    type='button'
                    onClick={() =>
                      editor?.chain().focus().toggleBulletList().run()
                    }
                    className={`hover:bg-muted/80 rounded-md p-1.5 transition-colors ${editor?.isActive('bulletList') ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}
                    title='Bullet List'>
                    <List className='size-4' />
                  </button>
                  <button
                    type='button'
                    onClick={() =>
                      editor?.chain().focus().toggleBlockquote().run()
                    }
                    className={`hover:bg-muted/80 rounded-md p-1.5 transition-colors ${editor?.isActive('blockquote') ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}
                    title='Quote'>
                    <Quote className='size-4' />
                  </button>
                </div>
                <EditorContent
                  editor={editor}
                  disabled={isPending}
                />

                <div className='bg-muted/20 border-border/50 flex justify-end gap-2 border-t p-3'>
                  <button
                    type='button'
                    onClick={() => {
                      setNewComment('');
                      editor?.commands.setContent('');
                    }}
                    className='text-foreground hover:bg-muted rounded-full px-4 py-2 text-sm font-medium transition-colors'>
                    Cancel
                  </button>
                  <button
                    type='submit'
                    disabled={!editor?.getText().trim() || isPending}
                    className='bg-primary text-primary-foreground disabled:bg-muted disabled:text-muted-foreground hover:bg-primary/90 rounded-full px-5 py-2 text-sm font-medium shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:shadow-none'>
                    {isPending ? 'Posting...' : 'Post Comment'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div className='bg-muted border-border mb-8 rounded-lg border p-4 text-center text-sm'>
            <p className='text-foreground'>
              <Link
                href={`/auth/signin?redirect=${encodeURIComponent(`/${postSlug}`)}`}
                className='text-primary font-semibold hover:underline'>
                Sign in
              </Link>{' '}
              to join the conversation and share your thoughts
            </p>
          </div>
        )}

        {/* Comments list */}
        <div className='space-y-6'>
          {comments.length === 0 ? (
            <p className='text-muted-foreground py-8 text-center italic'>
              No comments yet. Be the first to start the discussion!
            </p>
          ) : (
            comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                currentUserId={user?.id}
                onLike={() => toggleLikeRequest(comment.id)}
                onDelete={() => handleDelete(comment.id)}
                onReport={() => handleReport(comment.id)}
                onEdit={(newContent) => handleEdit(comment.id, newContent)}
              />
            ))
          )}
        </div>
      </div>
    </section>
  );
}

interface CommentItemProps {
  comment: Comment;
  currentUserId?: string;
  onLike: () => Promise<any>;
  onReply?: () => void;
  onDelete: () => void;
  onReport: () => void;
  onEdit: (newContent: string) => void;
}

function CommentItem({
  comment,
  currentUserId,
  onLike,
  onReply,
  onDelete,
  onReport,
  onEdit
}: CommentItemProps) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [StarterKit],
    content: comment.content,
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert min-h-[60px] w-full px-4 py-3 bg-transparent text-foreground placeholder-muted-foreground focus:outline-none resize-none'
      }
    }
  });

  const [isLiked, setIsLiked] = useState(comment.isLiked);
  const [likesCount, setLikesCount] = useState(comment.likesCount);

  const handleLike = async () => {
    const previousIsLiked = isLiked;
    const previousCount = likesCount;

    // Optimistic update
    setIsLiked(!previousIsLiked);
    setLikesCount((prev) => (previousIsLiked ? prev - 1 : prev + 1));

    try {
      const result = await onLike();
      if (!result?.success) {
        // Rollback
        setIsLiked(previousIsLiked);
        setLikesCount(previousCount);
      }
    } catch (error) {
      setIsLiked(previousIsLiked);
      setLikesCount(previousCount);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-start gap-4'>
        {/* Avatar */}
        <div className='flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-emerald-100 text-emerald-700'>
          {comment.users?.avatar_url ? (
            <Image
              src={comment.users.avatar_url}
              alt={comment.users.full_name || 'Avatar'}
              width={40}
              height={40}
              className='h-full w-full object-cover'
            />
          ) : (
            <span className='text-sm font-semibold'>
              {comment.users?.full_name?.charAt(0) || '?'}
            </span>
          )}
        </div>

        {/* Comment content */}
        <div className='min-w-0 flex-1'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-foreground flex items-center gap-2 font-semibold'>
                {comment.users?.full_name || 'Anonymous Analyst'}
              </p>
              <p className='text-muted-foreground flex items-center gap-1 text-xs'>
                {formatDistanceToNow(comment.created_at)}
                {comment.updated_at &&
                  comment.updated_at !== comment.created_at && (
                    <span className='text-[10px] italic opacity-80'>
                      (edited)
                    </span>
                  )}
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className='hover:bg-muted rounded p-1 transition-colors focus:outline-none'>
                  <MoreVertical className='text-muted-foreground size-4' />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='end'
                className='border-border/40 bg-card/95 z-[100] mt-1 w-40 rounded-xl p-1.5 shadow-xl backdrop-blur-md'>
                <DropdownMenuItem
                  onClick={onReport}
                  className='focus:bg-primary/10 focus:text-primary cursor-pointer gap-2 rounded-md py-2 text-sm transition-all'>
                  <Flag className='text-muted-foreground group-focus:text-primary size-4' />
                  Report
                </DropdownMenuItem>

                {currentUserId === comment.user_id && (
                  <>
                    <DropdownMenuSeparator className='bg-border/30' />
                    <DropdownMenuItem
                      onClick={() => setIsEditing(true)}
                      className='focus:bg-primary/10 focus:text-primary cursor-pointer gap-2 rounded-md py-2 text-sm transition-all'>
                      <Edit2 className='text-muted-foreground group-focus:text-primary size-4' />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={onDelete}
                      className='text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer gap-2 rounded-md py-2 text-sm transition-all'>
                      <Trash2 className='size-4' />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {isEditing ? (
            <div className='border-border bg-card focus-within:ring-primary/40 mt-3 flex-1 overflow-hidden rounded-xl border shadow-sm transition-all focus-within:ring-2'>
              <div className='bg-muted/40 border-border/50 flex items-center gap-1 border-b px-3 py-2'>
                <button
                  type='button'
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                  className={`hover:bg-muted/80 rounded-md p-1 transition-colors ${editor?.isActive('bold') ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}
                  title='Bold'>
                  <Bold className='size-3.5' />
                </button>
                <button
                  type='button'
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                  className={`hover:bg-muted/80 rounded-md p-1 transition-colors ${editor?.isActive('italic') ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}
                  title='Italic'>
                  <Italic className='size-3.5' />
                </button>
              </div>
              <EditorContent
                editor={editor}
                disabled={isPending}
              />

              <div className='bg-muted/20 border-border/50 flex justify-end gap-2 border-t p-2'>
                <button
                  type='button'
                  onClick={() => {
                    editor?.commands.setContent(comment.content);
                    setIsEditing(false);
                  }}
                  className='text-foreground hover:bg-muted rounded-full px-3 py-1.5 text-xs font-medium transition-colors'>
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={async () => {
                    const textContent = editor?.getText() || '';
                    if (!textContent.trim()) return;

                    setIsPending(true);
                    try {
                      await onEdit(editor?.getHTML() || '');
                      setIsEditing(false);
                    } finally {
                      setIsPending(false);
                    }
                  }}
                  disabled={
                    !editor?.getText().trim() ||
                    isPending ||
                    editor?.getHTML() === comment.content
                  }
                  className='bg-primary text-primary-foreground disabled:bg-muted disabled:text-muted-foreground hover:bg-primary/90 rounded-full px-4 py-1.5 text-xs font-medium shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:shadow-none'>
                  {isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <div
              className='text-foreground prose prose-sm dark:prose-invert prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-blockquote:my-2 prose-blockquote:border-primary/50 mt-3 max-w-none text-sm leading-relaxed whitespace-pre-wrap'
              dangerouslySetInnerHTML={{ __html: comment.content }}
            />
          )}

          {/* Actions - Currently mocked out visually without state */}
          <div className='mt-3 flex items-center gap-4 text-xs'>
            <button type="button"
              onClick={handleLike}
              className={`flex items-center gap-1.5 transition-all duration-300 ${
                isLiked
                  ? 'text-rose-500 hover:text-rose-600'
                  : 'text-muted-foreground hover:text-foreground'
              }`}>
              <motion.div
                whileTap={{ scale: 1.4 }}
                animate={{ scale: isLiked ? [1, 1.3, 1] : 1 }}
                transition={{ duration: 0.3 }}
                className='flex items-center'>
                <Heart
                  className={`size-4 transition-colors ${
                    isLiked ? 'fill-current' : 'fill-none'
                  }`}
                />
              </motion.div>
              <AnimatePresence mode='wait'>
                <motion.span
                  key={likesCount}
                  initial={{ y: 5, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -5, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className='min-w-[0.5rem] font-bold'>
                  {likesCount > 0 ? likesCount : ''}
                </motion.span>
              </AnimatePresence>
            </button>

            {onReply && (
              <button type="button" className='text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors'>
                <MessageCircle className='size-4' />
                Reply
              </button>
            )}

            <button type="button" className='text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors'>
              <Share2 className='size-4' />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
